import {
  Button,
  Divider,
  Heading,
  Switch,
  useFreshworksWidget,
} from "@stacklet/ui";

import { useUserContext } from "app/contexts/UserContext";

interface Props {
  /**
   * The feature being switched, named as the product does — it titles the row
   * and names the support ticket, e.g. "Jun0", "Stacklet MCP".
   */
  feature: string;
  /** Whether this deployment has the feature configured. */
  isEnabled: boolean;
}

/**
 * The enablement row the Control+ settings pages share: a switch that only
 * reports state, and — while the feature is unavailable — what it costs and a
 * way to ask about it.
 *
 * These features are turned on by a deployment change rather than a console
 * one, so there is nothing here for a user to toggle. The switch exists to
 * answer "do we have this?", which is why it is always disabled.
 */
export function ControlPlusEnablement({ feature, isEnabled }: Props) {
  const { currentUser } = useUserContext();
  // Ticket fields the support widget opens with, so the request arrives
  // triaged and the user has nothing to type. `type` has to match an option of
  // the Freshdesk portal's ticket Type field exactly, or the widget ignores it.
  const { open: openSupportWidget } = useFreshworksWidget(
    currentUser.email ?? "",
    currentUser.displayName ?? "",
    {
      subject: `${feature} enablement inquiry`,
      description: `I'd like to learn more about Control+ and enabling ${feature} in Stacklet.`,
      type: "Standard Support Request",
    },
  );

  return (
    <div className="flex flex-col gap-xs">
      <div className="flex items-center justify-between gap-lg pr-xxs">
        {/*
          An h2 styled down to label size: it opens the page's first section
          under the h1, so the outline needs the level even where the design
          does not want the weight.
        */}
        <Heading
          className="text-label-medium-strong text-text-primary"
          level={2}
        >
          Enable {feature}
        </Heading>
        <span className="text-body-small text-text-secondary">
          {isEnabled ? "Included in your subscription" : "Requires Control+"}
        </span>
      </div>

      <Divider />

      <div className="flex items-center justify-between gap-lg">
        <Switch isSelected={isEnabled} position="trailing" isDisabled>
          Enabled
        </Switch>
        {/*
          Styled as a link but built as a button: it opens the support widget
          in place rather than navigating, so the app Link wrapper — which
          needs a `to` or `href` — is the wrong element.
        */}
        {isEnabled ? null : (
          <Button
            className="text-link-small"
            onPress={openSupportWidget}
            variant="link"
          >
            Contact Support
          </Button>
        )}
      </div>
    </div>
  );
}
